import {
  actionCardCommands,
  teammateMessageActionCardKind,
} from '@littlebird/action-cards';
import { type ActionCard } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Button, IconButton, XStack, YStack } from '@/components/ui';
import TextArea from '@/components/ui/TextArea';
import { registerCardEditsSaver } from '@/features/action-cards-view/cardPendingEdits';
import { surfaceOutcome } from '@/features/action-cards/commands';
import { WikiUserAvatar } from '@/features/people-wiki/components/user-avatar';
import { cn } from '@/lib/utils';

import { HivemindAnswerTable } from './hivemind-conversations-table';
import { HivemindIncomingQuote } from './hivemind-incoming-quote';
import { saveText, typedKeyOf, typedText } from './hivemind-typed-text';
import { useHivemindSendAll } from './use-hivemind-send-all';

import type { HivemindGroup } from './use-hivemind-group';

const nameOf = (card: ActionCard) =>
  teammateMessageActionCardKind.decode(card.content)?.teammate;

type Sending = ReturnType<typeof useHivemindSendAll>;

// The messages and answers stay in the chat: this only takes the message off the Feed
const markAnswersSeen = async (cards: readonly ActionCard[]) => {
  const outcomes = await Promise.all(
    cards.map(({ id }) => actionCardCommands.acknowledge(id))
  );
  outcomes.forEach(surfaceOutcome);
};

const MessageEditor = observer(function MessageEditor({
  group: { cards, rows, text: draft, incomingText },
  sending: { isSending, sendAll, refusals, clearRefusals },
}: {
  readonly group: HivemindGroup;
  readonly sending: Sending;
}) {
  const typedKey = typedKeyOf(cards[0]!);
  // What the user typed, kept until the drafts hold it. Until then the editor follows the drafts, so a change of their
  // text from another device shows here, and what the user typed is not replaced by it
  const [typed, setTyped] = useState<string | null>(
    () => typedText.get(typedKey) ?? null
  );
  if (typed === draft) setTyped(null);
  const text = typed ?? draft;
  const drafts = cards.filter(({ status }) => status === 'draft');
  const idKey = drafts.map(({ id }) => id).join();
  const [removing, setRemoving] = useState(false);
  const [onlyCard] = cards.length === 1 ? cards : [];

  // One person left is no group, so the edit goes into that person's draft before the other leaves, and the editor
  // stays locked until then. A save that does not go through keeps everyone on the list
  const remove = async (card: ActionCard) => {
    setRemoving(true);
    try {
      if (cards.length <= 2) {
        const saves = await Promise.all(
          drafts
            .filter(({ id }) => id !== card.id)
            .map(({ id }) =>
              saveText(id, text).catch(() => ({
                saved: false,
                revision: null,
              }))
            )
        );
        if (!saves.every(({ saved }) => saved)) {
          toast.error("Couldn't save your edit, so no one was removed.");
          return;
        }
        // The saved drafts hold the text now, and a key made from the text they held no longer names it
        typedText.delete(typedKey);
      }
      surfaceOutcome(await actionCardCommands.dismiss(card.id));
    } finally {
      setRemoving(false);
    }
  };

  const canSend = !isSending && !removing && text.trim().length > 0;

  useEffect(() => {
    const unregister = idKey
      .split(',')
      .map((id) => registerCardEditsSaver(id, () => saveText(id, text)));
    return () => unregister.forEach((stop) => stop());
  }, [idKey, text]);

  return (
    <YStack className="gap-3">
      {incomingText && <HivemindIncomingQuote text={incomingText} />}
      <TextArea
        aria-label="Message"
        className="max-h-72 min-h-20 resize-none [field-sizing:content]"
        value={text}
        disabled={removing}
        onChange={(event) => {
          typedText.set(typedKey, event.target.value);
          setTyped(event.target.value);
          clearRefusals();
        }}
        onBlur={() => {
          // A reply is one card, so what was typed is saved as the user leaves the field, and survives a restart
          if (incomingText && onlyCard) void saveText(onlyCard.id, text);
        }}
        onKeyDown={(event) => {
          if (
            event.key === 'Enter' &&
            (event.metaKey || event.ctrlKey) &&
            canSend
          ) {
            event.preventDefault();
            event.stopPropagation();
            void sendAll();
          }
        }}
      />
      <XStack className="flex-wrap items-center gap-2">
        {/* A reply has one person, named in the title */}
        {!incomingText &&
          cards.map((card) => {
            const person = nameOf(card);
            const name = rows.find(({ id }) => id === card.id)?.name;
            const left = card.status !== 'draft';
            return (
              person &&
              name && (
                <XStack
                  key={card.id}
                  className={cn(
                    'items-center gap-1.5 rounded-full border bg-surface py-0.5 pl-1 text-sm text-foreground',
                    left ? 'pr-2 opacity-60' : 'pr-1',
                    refusals[card.id] ? 'border-danger-8' : 'border-border'
                  )}
                >
                  <WikiUserAvatar
                    userId={person.id}
                    name={name}
                    className="size-5 text-[0.5625rem]"
                  />
                  {left
                    ? `${name} · ${rows.find(({ id }) => id === card.id)?.status ?? 'Withdrawn'}`
                    : name}
                  {!left && !onlyCard && (
                    <button
                      type="button"
                      aria-label={`Remove ${name}`}
                      disabled={isSending || removing}
                      className="flex size-5 items-center justify-center rounded-full text-foreground-muted hover:bg-surface-hover hover:text-foreground focus-ring disabled:opacity-50"
                      onClick={() => void remove(card)}
                    >
                      <Icons.cancel className="size-3" />
                    </button>
                  )}
                </XStack>
              )
            );
          })}
      </XStack>
      {drafts
        .filter(({ id }) => refusals[id] !== undefined)
        .map((card) => (
          <p key={card.id} role="alert" className="text-sm text-danger">
            {`${rows.find(({ id }) => id === card.id)?.name ?? 'This person'}: ${refusals[card.id]}`}
          </p>
        ))}
      {!incomingText && (
        <p className="text-sm text-foreground-muted">
          Nothing is sent until you approve this message and recipient list.
        </p>
      )}
      <XStack className="items-center gap-2">
        <Button
          size="sm"
          data-card-primary
          disabled={!canSend}
          onClick={() => void sendAll()}
        >
          {isSending
            ? 'Sending…'
            : onlyCard
              ? 'Send'
              : `Send to ${drafts.length}`}
        </Button>
        {/* One person has no chip to take off, so the message is discarded here */}
        {onlyCard && (
          <IconButton
            icon={Icons.archive}
            variant="outline"
            tooltip="Discard"
            disabled={isSending || removing}
            onClick={() => {
              typedText.delete(typedKey);
              void actionCardCommands.dismiss(onlyCard.id).then(surfaceOutcome);
            }}
          />
        )}
      </XStack>
    </YStack>
  );
});

/** A message to one person or several: edited and sent from one place, then its answers as they come in */
export const HivemindGroupContent = observer(function HivemindGroupContent({
  group,
  clampAnswers,
  onOpenChat,
}: {
  readonly group: HivemindGroup;
  readonly clampAnswers: boolean;
  readonly onOpenChat?: () => void;
}) {
  'use no memo';
  // The state of a send outlives the editor, so a person it did not reach keeps its reason in the table
  const unsent = group.cards.filter(({ status }) => status === 'draft');
  const sending = useHivemindSendAll(group.chatId, unsent);
  if (group.isDraft) return <MessageEditor group={group} sending={sending} />;
  if (group.incomingText) {
    // A reply that went out needs no more than its text. One that did not says why
    const status = group.rows[0]?.status;
    return (
      <YStack data-plain-body="" className="gap-2">
        <p className="whitespace-pre-line text-sm text-foreground">
          {group.text}
        </p>
        {status && status !== 'Sent' && (
          <p className="text-sm text-foreground-muted">{status}</p>
        )}
      </YStack>
    );
  }
  const rows = group.rows.map((row) => {
    if (!unsent.some(({ id }) => id === row.id)) return row;
    const refusal = sending.refusals[row.id];
    return {
      ...row,
      status: sending.isSending
        ? 'Sending…'
        : refusal
          ? `Not sent · ${refusal}`
          : row.status,
    };
  });
  const isSingleRecipient = group.rows.length === 1;
  const row = rows[0];
  const status = row?.status;
  return (
    <YStack data-plain-body="" className="gap-2">
      <p className="whitespace-pre-line text-sm text-foreground">
        {group.text}
      </p>
      {isSingleRecipient ? (
        <>
          {clampAnswers && row && row.answer !== null && (
            <>
              <p className="text-sm text-foreground-muted">
                {row.name} answered
              </p>
              <p className="line-clamp-3 whitespace-pre-line text-sm text-foreground">
                {row.answer}
              </p>
            </>
          )}
          {status && status !== 'Waiting' && status !== 'Answered' && (
            <p className="text-sm text-foreground-muted">{status}</p>
          )}
        </>
      ) : (
        <HivemindAnswerTable rows={rows} clampAnswers={clampAnswers} />
      )}
      {(group.unseenAnswers.length > 0 ||
        (!isSingleRecipient && onOpenChat)) && (
        <XStack className="items-center gap-2">
          {group.unseenAnswers.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => void markAnswersSeen(group.unseenAnswers)}
            >
              Got it
            </Button>
          )}
          {!isSingleRecipient && onOpenChat && (
            <Button size="sm" variant="outline" onClick={onOpenChat}>
              Open in chat
            </Button>
          )}
        </XStack>
      )}
      {unsent.length > 0 && (
        <XStack>
          <Button
            size="sm"
            disabled={sending.isSending}
            onClick={() => void sending.sendAll()}
          >
            {sending.isSending ? 'Sending…' : `Send to ${unsent.length}`}
          </Button>
        </XStack>
      )}
    </YStack>
  );
});

/** The feed row's icon for a message: the first person and, for several, a count of the rest, sized to the row's icon tile in every layout */
export const HivemindGroupIcon = ({ group }: { group: HivemindGroup }) => {
  const [first] = group.rows;
  return (
    <span
      role="img"
      aria-label={group.title}
      className="relative flex size-[var(--ac-row-tile)] shrink-0"
    >
      {first && (
        <WikiUserAvatar
          userId={first.personId}
          name={first.name}
          className="size-[calc(var(--ac-row-tile)*0.8)] text-[length:calc(var(--ac-row-tile)*0.35)]"
        />
      )}
      {group.rows.length > 1 && (
        <span
          aria-hidden
          className="absolute bottom-0 right-0 flex h-[calc(var(--ac-row-tile)*0.5)] min-w-[calc(var(--ac-row-tile)*0.5)] items-center justify-center rounded-full bg-surface-active px-px text-[length:calc(var(--ac-row-tile)*0.3)] font-semibold text-foreground ring-2 ring-background"
        >
          {`+${group.rows.length - 1}`}
        </span>
      )}
    </span>
  );
};
